/**
 * Shop Dashboard Component
 * Shows claimed shops with verification status and allows verification flow
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Store, CheckCircle, Clock, AlertCircle, Key, ExternalLink, BarChart3, Crown, Trash2, Megaphone } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/lib/authContext";
import { getAuthHeader, authenticatedFetch } from "@/lib/authHelpers";
import { EditShopModal } from "./EditShopModal";
import { ShopStats } from "./ShopStats";
import { PostComposerModal } from "./PostComposerModal";
import { useI18n } from "@/lib/i18n";
import { detectUserCountry } from "@/lib/countryDetection";
import { generateShopSlug } from "@/lib/slugify";
import { useShopOwnerFeatures } from "@/lib/featureFlags";

interface Shop {
  id: string;
  name: string;
  address: string;
  city: string;
  province?: string | null;
  slug?: string | null;
  postal_code: string;
  has_charity: boolean;
  verification_status?: "unverified" | "pending_letter" | "verified";
  verification_code?: string | null;
  plan_status?: "inactive" | "active" | "past_due" | "canceled";
}

async function fetchClaimedShops(): Promise<Shop[]> {
  const headers = await getAuthHeader();
  if (!headers) {
    throw new Error("Not authenticated");
  }

  // Fetch shops claimed by current user
  const response = await authenticatedFetch("/api/users/me/shops", {
    headers,
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to fetch claimed shops");
  }

  return response.json();
}

async function startVerification(shopId: string): Promise<{ url: string }> {
  const headers = await getAuthHeader();
  if (!headers) {
    throw new Error("Not authenticated");
  }

  const response = await authenticatedFetch(`/api/shops/${shopId}/start-verification`, {
    method: "POST",
    headers: {
      ...headers,
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ countryCode: detectUserCountry() }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Failed to start verification" }));
    throw new Error(error.error || "Failed to start verification");
  }

  return response.json();
}

async function verifyCode(shopId: string, code: string): Promise<{ success: boolean; status: string }> {
  const headers = await getAuthHeader();
  if (!headers) {
    throw new Error("Not authenticated");
  }

  const response = await authenticatedFetch(`/api/shops/${shopId}/verify-code`, {
    method: "POST",
    headers: {
      ...headers,
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Failed to verify code" }));
    throw new Error(error.error || "Failed to verify code");
  }

  return response.json();
}

export function ShopDashboard() {
  const { t } = useI18n();
  const { user, isLoggedIn } = useAuth();
  const { canSeeShopOwner } = useShopOwnerFeatures();
  const queryClient = useQueryClient();
  const [verificationCodes, setVerificationCodes] = useState<Record<string, string>>({});
  const [verificationErrors, setVerificationErrors] = useState<Record<string, string>>({});
  const [editingShop, setEditingShop] = useState<Shop | null>(null);
  const [showStats, setShowStats] = useState<Record<string, boolean>>({});
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerShopId, setComposerShopId] = useState("");

  // Hidden when shop owner features are disabled
  if (!canSeeShopOwner) return null;

  const getShopDetailUrl = (shop: Shop) => {
    if (shop.slug && typeof shop.slug === "string" && shop.slug.trim().length > 0) {
      return `/kringloopwinkels/${shop.slug}`;
    }
    if (shop.province && shop.city && shop.name) {
      return `/kringloopwinkels/${generateShopSlug(shop.province, shop.city, shop.name)}`;
    }
    return `/shops/${shop.id}`;
  };

  const { data: shops = [], isLoading, refetch } = useQuery({
    queryKey: ["claimed-shops", user?.id],
    queryFn: fetchClaimedShops,
    enabled: isLoggedIn && !!user,
  });

  const startVerificationMutation = useMutation({
    mutationFn: startVerification,
    onSuccess: (data, shopId) => {
      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      console.error("Verification start error:", error);
    },
  });

  const verifyCodeMutation = useMutation({
    mutationFn: ({ shopId, code }: { shopId: string; code: string }) => verifyCode(shopId, code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["claimed-shops"] });
      // Clear verification code input
      setVerificationCodes({});
      setVerificationErrors({});
    },
    onError: (error: Error, variables) => {
      setVerificationErrors({
        ...verificationErrors,
        [variables.shopId]: error.message,
      });
    },
  });

  const handleStartVerification = (shopId: string) => {
    startVerificationMutation.mutate(shopId);
  };

  const handleVerifyCode = (shopId: string) => {
    const code = verificationCodes[shopId]?.trim().toUpperCase();
    if (!code || code.length < 4) {
      setVerificationErrors({
        ...verificationErrors,
        [shopId]: t("shopDashboard.validVerificationCode"),
      });
      return;
    }

    verifyCodeMutation.mutate({ shopId, code });
  };

  if (!isLoggedIn) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Store className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-semibold text-lg mb-2">{t("shopDashboard.notLoggedIn")}</h3>
          <p className="text-muted-foreground">
            {t("shopDashboard.loginToManage")}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">{t("shopDashboard.loadingShops")}</p>
        </CardContent>
      </Card>
    );
  }

  if (shops.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Store className="h-5 w-5" />
            {t("shopDashboard.title")}
          </CardTitle>
          <CardDescription>
            {t("shopDashboard.manageClaimed")}
          </CardDescription>
        </CardHeader>
        <CardContent className="py-12 text-center">
          <Store className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-semibold text-lg mb-2">{t("shopDashboard.noneClaimed")}</h3>
          <p className="text-muted-foreground mb-4">
            {t("shopDashboard.claimToManage")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Store className="h-5 w-5" />
          {t("shopDashboard.title")}
        </CardTitle>
        <CardDescription>
          {t("shopDashboard.manageAndVerify")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {shops.map((shop) => {
            const isKringloop = shop.has_charity;
            const verificationStatus = shop.verification_status || "unverified";
            const isVerified = verificationStatus === "verified";
            const isPending = verificationStatus === "pending_letter";

            return (
              <div
                key={shop.id}
                className="border rounded-lg p-4 space-y-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 group cursor-pointer" onClick={() => window.location.href = getShopDetailUrl(shop)}>
                      <h4 className="font-semibold group-hover:text-primary group-hover:underline transition-colors">{shop.name}</h4>
                      {isKringloop && (
                        <Badge variant="secondary">{t("shops.title")}</Badge>
                      )}
                      <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {shop.address && shop.address.includes(shop.postal_code) ? shop.address : `${shop.address}, ${shop.postal_code} ${shop.city}`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {isVerified && (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => window.location.href = getShopDetailUrl(shop)}
                          className="flex items-center gap-2"
                        >
                          <Store className="h-4 w-4" />
                          {t("shopDashboard.viewProfile")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowStats(prev => ({ ...prev, [shop.id]: !prev[shop.id] }))}
                          className="flex items-center gap-2"
                        >
                          <BarChart3 className="h-4 w-4" />
                          {showStats[shop.id] ? t("shopDashboard.hideStats") : t("shopDashboard.stats")}
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => {
                            setComposerShopId(shop.id);
                            setComposerOpen(true);
                          }}
                          className="flex items-center gap-2 bg-primary hover:bg-primary/90"
                        >
                          <Megaphone className="h-4 w-4" />
                          {t("shopDashboard.postUpdate") || "Plaats Update"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingShop(shop)}
                        >
                          {t("shopDashboard.manage")}
                        </Button>
                      </>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive/90 hover:bg-destructive/10"
                      onClick={async () => {
                        if (confirm(t("shopDashboard.confirmUnclaim"))) {
                          try {
                            const headers = await getAuthHeader();
                            const res = await fetch(`/api/shops/${shop.id}/unclaim`, {
                              method: "POST",
                              headers: headers || {}
                            });
                            if (res.ok) {
                              queryClient.invalidateQueries({ queryKey: ["claimed-shops"] });
                            }
                          } catch (e) {
                            console.error(e);
                          }
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>


                {/* Kringloop Verification Flow */}
                {(isKringloop || verificationStatus !== "unverified") && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      {isVerified ? (
                        <>
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <span className="text-sm font-medium text-green-600">
                            {t("shopCard.verified")}
                          </span>
                        </>
                      ) : isPending ? (
                        <>
                          <Clock className="h-5 w-5 text-yellow-600" />
                          <span className="text-sm font-medium text-yellow-600">
                            {t("shopDashboard.waitingLetter")}
                          </span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-5 w-5 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {t("shopDashboard.notVerified")}
                          </span>
                        </>
                      )}
                    </div>

                    {!isVerified && (
                      <div className="space-y-2">
                        {verificationStatus === "unverified" && (
                          <Button
                            onClick={() => handleStartVerification(shop.id)}
                            disabled={startVerificationMutation.isPending}
                            className="w-full"
                          >
                            <Key className="h-4 w-4 mr-2" />
                            {t("shopDashboard.startVerification")}
                          </Button>
                        )}

                        {isPending && (
                          <div className="space-y-2">
                            <Alert>
                              <AlertDescription>
                                {t("shopDashboard.letterOnTheWay")}
                              </AlertDescription>
                            </Alert>
                            <div className="space-y-2">
                              <Label htmlFor={`code-${shop.id}`}>
                                {t("shopDashboard.verificationCode")}
                              </Label>
                              <div className="flex gap-2">
                                <Input
                                  id={`code-${shop.id}`}
                                  placeholder={t("shopDashboard.codeExample")}
                                  value={verificationCodes[shop.id] || ""}
                                  onChange={(e) =>
                                    setVerificationCodes({
                                      ...verificationCodes,
                                      [shop.id]: e.target.value,
                                    })
                                  }
                                  className="flex-1 uppercase"
                                  maxLength={10}
                                />
                                <Button
                                  onClick={() => handleVerifyCode(shop.id)}
                                  disabled={verifyCodeMutation.isPending}
                                >
                                  {t("shopDashboard.verify")}
                                </Button>
                              </div>
                              {verificationErrors[shop.id] && (
                                <p className="text-sm text-red-600">
                                  {verificationErrors[shop.id]}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {isVerified && (
                      <Alert>
                        <CheckCircle className="h-4 w-4" />
                        <AlertDescription>
                          {t("shopDashboard.verifiedAllFeatures")}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}
                {/* Statistics Section */}
                {showStats[shop.id] && (
                  <ShopStats shopId={shop.id} shopName={shop.name} />
                )}
              </div>
            );
          })}
        </div>
      </CardContent>

      {editingShop && (
        <EditShopModal
          shop={editingShop}
          open={!!editingShop}
          onOpenChange={(open) => !open && setEditingShop(null)}
        />
      )}

      <PostComposerModal
        open={composerOpen}
        onOpenChange={setComposerOpen}
        defaultPostAs={composerShopId}
      />
    </Card>
  );
}
